import $ from 'jquery';
import set from 'lodash/set';
import moment from 'moment';
import React, {
  CSSProperties,
  FunctionComponent,
  useEffect,
  useState,
} from 'react';
import QRCode from 'react-qr-code';
import {useDispatch} from 'react-redux';
import swal from 'sweetalert2';
import {InputLabelEditor} from '../../components/InputLabelEditor';
import {NewsPostEditor} from '../../components/NewsPostEditor';
import {getEndpoint, getHeaders} from '../../helpers/endpoint';
import {INewsFeed} from '../../types/newsFeed';

export const newsPostStatus = {
  1: 'Created',
  2: 'Scheduled',
  3: 'Published',
};

const styles: Record<string, CSSProperties> = {
  container: {
    display: 'flex',
    flexDirection: 'row',
    margin: 16,
  },
  titleContainer: {
    display: 'flex',
    height: 43,
    backgroundColor: 'white',
    width: '100%',
    alignItems: 'center',
    fontSize: 18,
    paddingLeft: 16,
    justifyContent: 'space-between',
  },
  flex3: {
    flex: 3,
  },
  flex1: {
    flex: 1,
  },
  saveButton: {
    height: 48,
    width: 150,
    color: '#4054CC',
    borderRadius: 4,
    backgroundColor: 'transparent',
    borderWidth: 1,
    borderColor: '#4054CC',
    fontWeight: 'bold',
  },
  submitButton: {
    height: 48,
    width: 150,
    color: 'white',
    borderRadius: 4,
    backgroundColor: '#4054CC',
    fontWeight: 'bold',
  },
  buttonContainer: {
    display: 'flex',
    justifyContent: 'flex-end',
    flexDirection: 'row',
    marginTop: 32,
  },
  disabledButton: {
    height: 48,
    width: 150,
    color: 'white',
    borderRadius: 4,
    backgroundColor: '#959DAF',
    fontWeight: 'bold',
    borderWidth: 0,
  },
  updatedAtContainer: {
    marginRight: 16,
  },
};

export const getNewsPostById = (
  newsPostId: string,
  onSuccess: (response: INewsFeed) => void,
) => {
  return (_dispatch, getState) => {
    return fetch(getEndpoint('getNewsPostById', newsPostId), {
      method: 'GET',
      headers: getHeaders(getState().authentication),
    })
      .then(response => response.json())
      .then(data => {
        if (data.error) {
        } else {
          onSuccess(data);
        }
      })
      .catch(error => {});
  };
};

export const updateNewsPost = (
  payload: INewsFeed,
  onSuccess?: (response: INewsFeed) => void,
) => {
  return (_dispatch, getState) => {
    return fetch(getEndpoint('updateNewsPost', payload.id), {
      method: 'PATCH',
      headers: getHeaders(getState().authentication),
      body: JSON.stringify({...payload, publishedDate: undefined}),
    })
      .then(response => response.json())
      .then(data => {
        if (data.error) {
          swal({
            title: `Oops...`,
            type: 'error',
            html: `Something went wrong !<br/>${data.message}<br/>`,
          });
        } else {
          // eslint-disable-next-line no-unused-expressions
          onSuccess?.(data);
        }
      })
      .catch(error => {});
  };
};

export const publishNewsPostById = (
  payload: INewsFeed,
  onSuccess?: (response: INewsFeed) => void,
) => {
  return (_dispatch, getState) => {
    return fetch(getEndpoint('publishNewsPost', payload.id), {
      method: 'PATCH',
      headers: getHeaders(getState().authentication),
      body: JSON.stringify(payload),
    })
      .then(response => response.json())
      .then(data => {
        if (data.error) {
          swal({
            title: `Oops...`,
            type: 'error',
            html: `Something went wrong !<br/>${data.message}<br/>`,
          });
        } else {
          // eslint-disable-next-line no-unused-expressions
          onSuccess?.(data);
        }
      })
      .catch(error => {});
  };
};

export const UpdateNewsPost: FunctionComponent = ({match}) => {
  const dispatch = useDispatch();
  const [errorForSubmit, setErrorForSubmit] = useState(false);
  const [newsPost, setNewsPost] = useState<INewsFeed | undefined>(undefined);
  const onChange = (key: string, value: string | number | undefined) => {
    newsPost && setNewsPost({...set(newsPost, key, value)});
  };

  const onSuccess = (response: INewsFeed, successModal?: boolean) => {
    if (successModal) {
      swal({
        title: `Sauvegardé`,
        type: 'success',
        html: ``,
      });
    }
    setNewsPost(response);
  };

  useEffect(() => {
    dispatch(getNewsPostById(match.params.newsPostId, onSuccess));
  }, []);

  const onSave = (successModal?: boolean) => {
    if (newsPost) {
      dispatch(
        updateNewsPost(newsPost, response => onSuccess(response, successModal)),
      );
    }
  };

  const isSubmittable =
    !errorForSubmit &&
    newsPost?.name &&
    newsPost.publishedTimestamp &&
    ((newsPost.localization?.['fr-FR']?.title &&
      newsPost.localization['fr-FR'].content) ||
      (newsPost.localization?.['es-ES']?.title &&
        newsPost.localization['es-ES'].content) ||
      (newsPost.localization?.['en-GB']?.title &&
        newsPost.localization['en-GB'].content));

  let disabled = false;
  if (newsPost !== undefined) {
    if (!newsPost.name) disabled = true;
    if (newsPost.localization) {
      Object.keys(newsPost.localization).forEach(key => {
        if (newsPost.localization && newsPost.localization[key]) {
          if (newsPost.localization[key].socialEmbed) {
            Object.keys(newsPost.localization[key].socialEmbed || {}).forEach(
              socialKey => {
                if (
                  newsPost.localization[key].socialEmbed[socialKey] &&
                  socialKey !== 'youtube'
                ) {
                  if (!newsPost.localization[key].socialEmbed[socialKey].html) {
                    disabled = true;
                  }
                }
              },
            );
          }
        }
      });
    }
  }

  const onClickValidate = () => {
    onSave();
  };

  const isScheduled =
    newsPost?.status === 2 &&
    newsPost.publishedTimestamp &&
    moment(newsPost.publishedTimestamp).isSameOrBefore(moment());

  return (
    <div style={styles.container}>
      <div id="validateSendNewsPost" className="modal fade" role="dialog">
        <div className="modal-dialog">
          <div className="modal-body">
            <div className="modal-content">
              <div className="modal-header">
                <button type="button" className="close" data-dismiss="modal">
                  &times;
                </button>
                <h4 className="modal-title">Valider l’envoi de cette news ?</h4>
              </div>
              <div className="modal-body">
                <InputLabelEditor title="DATE / HEURE" />
                {moment(newsPost?.publishedTimestamp).isSameOrBefore(
                  moment(),
                ) ? (
                  <div>
                    <span style={{color: '#696773'}}>Immédiat: </span>
                    <span style={{color: 'red'}}>
                      <i
                        style={{marginRight: 8}}
                        className="fa fa-exclamation-triangle"
                      />
                      La news partira dès la planification valider
                    </span>
                  </div>
                ) : (
                  <div>
                    <span style={{color: '#696773'}}>Différé: </span>
                    <span style={{fontWeight: 'bold'}}>
                      {moment(newsPost?.publishedTimestamp).format(
                        'DD/MM/YYYY HH:mm',
                      )}
                    </span>
                  </div>
                )}
                <div
                  style={{
                    marginTop: 32,
                    display: 'flex',
                    alignItems: 'center',
                  }}>
                  <button
                    onClick={() => {
                      if (newsPost) {
                        dispatch(
                          publishNewsPostById(
                            newsPost,
                            (response: INewsFeed) => {
                              setNewsPost(response);
                              $('#validateSendNewsPost').modal('hide');
                            },
                          ),
                        );
                      }
                    }}
                    style={{...styles.submitButton, marginRight: 8}}>
                    Valider
                  </button>
                  <button type="button" data-dismiss="modal" className="btn">
                    Annuler
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      <div style={styles.flex3}>
        <div style={styles.titleContainer}>
          <div>Update news post</div>
          <div style={{marginRight: 8}}>{`status: ${
            newsPost?.status &&
            newsPostStatus[isScheduled ? 3 : newsPost.status]
          }`}</div>
        </div>
        {newsPost && (
          <NewsPostEditor
            onChange={onChange}
            data={newsPost}
            setErrorForSubmit={setErrorForSubmit}
          />
        )}
        <div style={styles.buttonContainer}>
          <div style={styles.updatedAtContainer}>
            <div style={{color: '#696773'}}>Dernière sauvegarde :</div>
            <div>
              {!!newsPost?.updatedAt &&
                moment(newsPost?.updatedAt).format('DD/MM à HH:mm')}
            </div>
          </div>
          <button
            disabled={!newsPost?.name || errorForSubmit}
            style={
              !errorForSubmit && !disabled
                ? styles.saveButton
                : styles.disabledButton
            }
            onClick={() => onSave(true)}>
            Sauvegarder
          </button>
          {newsPost?.status === 1 && (
            <div style={{marginLeft: 16, position: 'relative'}}>
              <span
                style={{
                  position: 'absolute',
                  marginLeft: 8,
                  color: 'red',
                  top: -16,
                  right: 0,
                }}>
                *
              </span>
              <button
                data-toggle="modal"
                data-target="#validateSendNewsPost"
                disabled={!isSubmittable}
                style={
                  isSubmittable ? styles.submitButton : styles.disabledButton
                }
                onClick={onClickValidate}>
                Valider
              </button>
            </div>
          )}
        </div>
      </div>
      <div style={{...styles.flex1, display: 'flex', justifyContent: 'center'}}>
        <QRCode
          value={`${process.env.DEEPLINK_URL}/app/newsfeed/${newsPost?.id}`}
        />
      </div>
    </div>
  );
};



// WEBPACK FOOTER //
// ./src/pages/UpdateNewsPost/index.tsx